/**
 * Страница аналитики рассылок: история отправленных рассылок с фильтрацией
 * Дата: 30.01.2026 - Создано для TODO-046 (Аналитика рассылок)
 * 
 * СВЯЗАННЫЕ МОДУЛИ И ФАЙЛЫ:
 * - frontend/src/api/analytics.ts - API для работы с аналитикой
 * - internal/handler/analytics.go - бэкенд обработчик аналитики
 * - components/ui/SearchInput.tsx - унифицированный поиск
 * - components/ui/FilterSelect.tsx - унифицированный фильтр
 * - components/ui/DateRangePicker.tsx - выбор периода
 * - components/ui/EmptyState.tsx - унифицированное пустое состояние
 * - components/ui/LoadingState.tsx - унифицированная загрузка
 * 
 * КАК РАБОТАЕТ:
 * 1. Загружает список рассылок через analyticsApi.getBroadcasts()
 * 2. Фильтрует по датам, шаблонам, статусам и каналам
 * 3. Отображает summary карточки и таблицу рассылок
 * 4. Кнопка "Чат" переходит в /messages?phone={phone}
 * 
 * СХЕМА:
 * AnalyticsPage → analyticsApi.getBroadcasts() → backend API
 * AnalyticsPage → DateRangePicker → фильтрация по периоду
 * AnalyticsPage → FilterSelect → фильтрация по шаблону/статусу/каналу
 * AnalyticsPage → navigate('/messages?phone={phone}') → MessagesPage
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { logger } from '@/utils/logger';
import { MainLayout } from '@/components/layout/MainLayout';
import { SubsectionSwitch } from '@/components/ui/SubsectionSwitch';
import { LinearScreen } from '@/features/linear-canvas/LinearScreen';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent, 
  Button, 
  Badge, 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableHeader, 
  TableCell, 
  TenantSelector,
  DateRangePicker,
  FilterSelect,
  EmptyState,
  LoadingState,
  SearchInput,
} from '@/components/ui';
import { 
  analyticsApi, 
  BroadcastDTO, 
  BroadcastFilters, 
  ScheduledMessageDTO, 
  ScheduledFilters,
  AppointmentReminderDTO,
  AppointmentReminderFilters,
} from '@/api/analytics';
import { useTenantScope } from '@/hooks/useTenantScope';
import { formatDateTime, formatPhone } from '@/utils/formatters';
import { startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { MessageSquare, Send, CheckCircle, XCircle, Clock, CalendarClock, Bell, BellOff, MessageCircle } from 'lucide-react';

export function AnalyticsPage() {
  const navigate = useNavigate();
  const { tenantId, isSuperAdmin, selectedTenantId, setSelectedTenant, requireTenantSelection } = useTenantScope();
  
  // Вкладки: 'sent' (отправлено), 'scheduled' (запланировано), 'appointments' (по записям)
  const [activeTab, setActiveTab] = useState<'sent' | 'scheduled' | 'appointments'>('sent');
  
  // Общие состояния
  const [isLoading, setIsLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState<Date | null>(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [dateTo, setDateTo] = useState<Date | null>(endOfWeek(new Date(), { weekStartsOn: 1 }));
  const [searchQuery, setSearchQuery] = useState('');
  const [limit] = useState(20);
  const [offset, setOffset] = useState(0);

  // Состояния для отправленных
  const [broadcasts, setBroadcasts] = useState<BroadcastDTO[]>([]);
  const [broadcastTotal, setBroadcastTotal] = useState(0);
  const [broadcastSummary, setBroadcastSummary] = useState({ total: 0, delivered: 0, failed: 0, pending: 0 });
  const [templateFilter, _setTemplateFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [messengerFilter, setMessengerFilter] = useState<string>('all');

  // Состояния для запланированных
  const [scheduled, setScheduled] = useState<ScheduledMessageDTO[]>([]);
  const [scheduledTotal, setScheduledTotal] = useState(0);
  const [scheduledSummary, setScheduledSummary] = useState({ total: 0, pending: 0, sent: 0, failed: 0, skipped: 0 });
  const [scheduledStatusFilter, setScheduledStatusFilter] = useState<string>('all');

  // Состояния для напоминаний по записям
  const [appointments, setAppointments] = useState<AppointmentReminderDTO[]>([]);
  const [appointmentsTotal, setAppointmentsTotal] = useState(0);
  const [appointmentsSummary, setAppointmentsSummary] = useState({
    total_appointments: 0,
    reminder_24h_sent: 0, reminder_24h_total: 0,
    reminder_2h_sent: 0, reminder_2h_total: 0,
    feedback_sent: 0, feedback_total: 0,
  });
  const [appointmentStatusFilter, setAppointmentStatusFilter] = useState<string>('all');
  const [reminderFilter, setReminderFilter] = useState<string>('all');

  // Логирование просмотра страницы
  useEffect(() => {
    logger.info('page_view', { page: 'analytics' });
  }, []);

  // Загрузка данных в зависимости от активной вкладки
  useEffect(() => {
    if (!tenantId || !dateFrom || !dateTo) {
      setBroadcasts([]);
      setScheduled([]);
      setAppointments([]);
      return;
    }

    const loadData = async () => {
      setIsLoading(true);
      try {
        if (activeTab === 'sent') {
          // Загрузка отправленных
        const filters: BroadcastFilters = {
          tenant_id: tenantId,
          from_date: dateFrom.toISOString().split('T')[0],
          to_date: dateTo.toISOString().split('T')[0],
          template_id: templateFilter !== 'all' ? templateFilter : undefined,
          status: statusFilter,
          messenger: messengerFilter !== 'all' ? messengerFilter : undefined,
          limit,
          offset,
        };

        const response = await analyticsApi.getBroadcasts(filters);
        setBroadcasts(response.broadcasts);
          setBroadcastTotal(response.pagination.total);
          setBroadcastSummary(response.summary);
        logger.info('broadcasts_loaded', { 
          count: response.broadcasts.length,
          total: response.pagination.total,
          });
        } else if (activeTab === 'scheduled') {
          // Загрузка запланированных
          const filters: ScheduledFilters = {
            tenant_id: tenantId,
            from_date: dateFrom.toISOString().split('T')[0],
            to_date: dateTo.toISOString().split('T')[0],
            status: scheduledStatusFilter,
            search: searchQuery || undefined,
            limit,
            offset,
          };

          const response = await analyticsApi.getScheduled(filters);
          setScheduled(response.messages || []);
          setScheduledTotal(response.pagination.total);
          setScheduledSummary(response.summary);
          logger.info('scheduled_loaded', { 
            count: response.messages?.length || 0,
            total: response.pagination.total,
          });
        } else if (activeTab === 'appointments') {
          // Загрузка напоминаний по записям
          const filters: AppointmentReminderFilters = {
            tenant_id: tenantId,
            from_date: dateFrom.toISOString().split('T')[0],
            to_date: dateTo.toISOString().split('T')[0],
            status: appointmentStatusFilter,
            reminder: reminderFilter,
            search: searchQuery || undefined,
            limit,
            offset,
          };

          const response = await analyticsApi.getAppointmentReminders(filters);
          setAppointments(response.appointments || []);
          setAppointmentsTotal(response.pagination.total);
          setAppointmentsSummary(response.summary);
          logger.info('appointments_loaded', { 
            count: response.appointments?.length || 0,
            total: response.pagination.total,
          });
        }
      } catch (error: any) {
        logger.error('analytics_load_failed', { 
          tab: activeTab,
          error: error.response?.data?.error || error.message 
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [tenantId, dateFrom, dateTo, activeTab, templateFilter, statusFilter, messengerFilter, scheduledStatusFilter, appointmentStatusFilter, reminderFilter, searchQuery, limit, offset]);

  // Быстрые кнопки периода
  const handleQuickPeriod = (period: 'today' | 'week' | 'month') => {
    const now = new Date();
    switch (period) {
      case 'today':
        setDateFrom(startOfDay(now));
        setDateTo(endOfDay(now));
        break;
      case 'week':
        setDateFrom(startOfWeek(now, { weekStartsOn: 1 }));
        setDateTo(endOfWeek(now, { weekStartsOn: 1 }));
        break;
      case 'month':
        setDateFrom(startOfMonth(now));
        setDateTo(endOfMonth(now));
        break;
    }
    setOffset(0);
  };

  // Переход в чат
  const handleOpenChat = (phone: string) => {
    logger.info('analytics_chat_click', { phone });
    navigate(`/messages?phone=${encodeURIComponent(phone)}`);
  };

  // Получить статус badge для отправленных
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'delivered':
        return <Badge className="bg-green-100 text-green-800 inline-flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Доставлено</Badge>;
      case 'read':
        return <Badge className="bg-blue-100 text-blue-800 inline-flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Прочитано</Badge>;
      case 'sent':
        return <Badge className="bg-blue-100 text-blue-800 inline-flex items-center gap-1"><Send className="w-3 h-3" /> Отправлено</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800 inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> Ошибка</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> В ожидании</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  // Получить статус badge для запланированных
  const getScheduledStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Ожидает</Badge>;
      case 'sent':
        return <Badge className="bg-green-100 text-green-800 inline-flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Отправлено</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800 inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> Ошибка</Badge>;
      case 'skipped':
        return <Badge className="bg-gray-100 text-gray-800">Пропущено</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  // Получить канал badge
  const getMessengerBadge = (messenger: string) => {
    const colors: Record<string, string> = {
      whatsapp_waba: 'bg-green-100 text-green-800',
      whatsapp_regular: 'bg-green-100 text-green-800',
      telegram_bot: 'bg-blue-100 text-blue-800',
      telegram_client: 'bg-blue-100 text-blue-800',
      max: 'bg-purple-100 text-purple-800',
      sms: 'bg-gray-100 text-gray-800',
    };
    return <Badge className={colors[messenger] || 'bg-gray-100 text-gray-800'}>{messenger}</Badge>;
  };

  // Получить статус записи badge
  const getAppointmentStatusBadge = (status: string) => {
    switch (status) {
      case 'scheduled':
        return <Badge className="bg-blue-100 text-blue-800">Запланировано</Badge>;
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">Завершено</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-800">Отменено</Badge>;
      case 'no_show':
        return <Badge className="bg-orange-100 text-orange-800">Не явился</Badge>;
      case 'confirmed':
        return <Badge className="bg-teal-100 text-teal-800">Подтверждено</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  if (requireTenantSelection) {
    return (
      <MainLayout headerTitle="Аналитика рассылок" disableMainScroll>
        <LinearScreen
          eyebrow="Аналитика"
          title="Аналитика рассылок"
          lead="Выберите клинику, чтобы увидеть отправленные, запланированные и привязанные к записям сообщения."
          toolbar={
            <div className="max-w-xs">
              <TenantSelector
                value={selectedTenantId || ''}
                onChange={setSelectedTenant}
                required={false}
                showLabel={false}
              />
            </div>
          }
        >
          <EmptyState
            type="noData"
            title="Выберите клинику"
            description="Выберите клинику для просмотра аналитики"
          />
        </LinearScreen>
      </MainLayout>
    );
  }

  return (
    <MainLayout headerTitle="Аналитика рассылок" disableMainScroll>
      <LinearScreen
        eyebrow="Аналитика"
        title="Аналитика рассылок"
        lead={
          activeTab === 'sent'
            ? 'Уже отправленные сообщения: доставка и ответ.'
            : activeTab === 'scheduled'
              ? 'Сообщения, которые ещё ждут отправки.'
              : 'Сообщения, привязанные к записям на приём.'
        }
        toolbar={
          <div className="space-y-3">
            {isSuperAdmin && (
              <div className="max-w-xs">
                <TenantSelector
                  value={selectedTenantId || ''}
                  onChange={setSelectedTenant}
                  required={false}
                  showLabel={false}
                />
              </div>
            )}
            <SubsectionSwitch
              testId="analytics-section"
              value={activeTab}
              onChange={(id) => {
                setActiveTab(id as 'sent' | 'scheduled' | 'appointments');
                setOffset(0);
              }}
              items={[
                { id: 'sent', label: 'Отправлено' },
                { id: 'scheduled', label: 'Запланировано' },
                { id: 'appointments', label: 'По записям' },
              ]}
            />
          </div>
        }
      >
      <div>

        {/* Summary карточки - динамические в зависимости от вкладки */}
        {activeTab === 'sent' ? (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Send className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-neutral-600">Отправлено</p>
                    <p className="text-2xl font-bold">{broadcastSummary.total}</p>
                  </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-neutral-600">Доставлено</p>
                    <p className="text-2xl font-bold">{broadcastSummary.delivered}</p>
                  </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-neutral-600">Ошибки</p>
                    <p className="text-2xl font-bold">{broadcastSummary.failed}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Clock className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600">В ожидании</p>
                    <p className="text-2xl font-bold">{broadcastSummary.pending}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : activeTab === 'scheduled' ? (
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <CalendarClock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600">Всего</p>
                    <p className="text-2xl font-bold">{scheduledSummary.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                    <p className="text-sm text-neutral-600">Ожидают</p>
                    <p className="text-2xl font-bold">{scheduledSummary.pending}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600">Отправлено</p>
                    <p className="text-2xl font-bold">{scheduledSummary.sent}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <XCircle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600">Ошибки</p>
                    <p className="text-2xl font-bold">{scheduledSummary.failed}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : activeTab === 'appointments' ? (
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <CalendarClock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600">Записей</p>
                    <p className="text-2xl font-bold">{appointmentsSummary.total_appointments}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Bell className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600">24ч напоминания</p>
                    <p className="text-2xl font-bold">{appointmentsSummary.reminder_24h_sent}/{appointmentsSummary.reminder_24h_total}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Clock className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600">2ч напоминания</p>
                    <p className="text-2xl font-bold">{appointmentsSummary.reminder_2h_sent}/{appointmentsSummary.reminder_2h_total}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <MessageCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600">Обратная связь</p>
                    <p className="text-2xl font-bold">{appointmentsSummary.feedback_sent}/{appointmentsSummary.feedback_total}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>
              {activeTab === 'sent' ? 'История рассылок' : 
               activeTab === 'scheduled' ? 'Запланированные сообщения' : 
               'История напоминаний по записям'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Фильтры */}
            <div className="space-y-4 mb-6">
              {/* Период */}
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => handleQuickPeriod('today')}>
                    Сегодня
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleQuickPeriod('week')}>
                    Неделя
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleQuickPeriod('month')}>
                    Месяц
                  </Button>
                </div>
                <DateRangePicker
                  from={dateFrom}
                  to={dateTo}
                  onChange={(from, to) => {
                    setDateFrom(from);
                    setDateTo(to);
                    setOffset(0);
                  }}
                  presets={[
                    { label: 'Сегодня', from: startOfDay(new Date()), to: endOfDay(new Date()) },
                    { label: 'Неделя', from: startOfWeek(new Date(), { weekStartsOn: 1 }), to: endOfWeek(new Date(), { weekStartsOn: 1 }) },
                    { label: 'Месяц', from: startOfMonth(new Date()), to: endOfMonth(new Date()) },
                  ]}
                  testId="analytics-date-range"
                />
              </div>

              {/* Поиск по пациентам */}
              <div className="flex gap-4 items-end">
                <div className="w-64">
                  <SearchInput
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Поиск по телефону или ФИО..."
                    testId="analytics-search"
                  />
                </div>
                
                {/* Фильтры по статусу и каналу */}
                {activeTab === 'sent' ? (
                  <>
                <div className="w-48">
                  <FilterSelect
                    value={statusFilter}
                        onChange={(v) => { setStatusFilter(v); setOffset(0); }}
                    options={[
                      { value: 'all', label: 'Все статусы' },
                      { value: 'delivered', label: 'Доставлено' },
                      { value: 'failed', label: 'Ошибки' },
                      { value: 'pending', label: 'В ожидании' },
                    ]}
                    testId="analytics-status-filter"
                  />
                </div>
                <div className="w-48">
                  <FilterSelect
                    value={messengerFilter}
                        onChange={(v) => { setMessengerFilter(v); setOffset(0); }}
                    options={[
                      { value: 'all', label: 'Все каналы' },
                      { value: 'whatsapp_waba', label: 'WhatsApp WABA' },
                      { value: 'whatsapp_regular', label: 'WhatsApp Regular' },
                      { value: 'telegram_bot', label: 'Telegram Bot' },
                      { value: 'telegram_client', label: 'Telegram Client' },
                      { value: 'max', label: 'MAX' },
                      { value: 'sms', label: 'SMS' },
                    ]}
                    testId="analytics-messenger-filter"
                  />
                </div>
                  </>
                ) : activeTab === 'scheduled' ? (
                  <div className="w-48">
                    <FilterSelect
                      value={scheduledStatusFilter}
                      onChange={(v) => { setScheduledStatusFilter(v); setOffset(0); }}
                      options={[
                        { value: 'all', label: 'Все статусы' },
                        { value: 'pending', label: 'Ожидает' },
                        { value: 'sent', label: 'Отправлено' },
                        { value: 'failed', label: 'Ошибка' },
                        { value: 'skipped', label: 'Пропущено' },
                      ]}
                      testId="analytics-scheduled-status-filter"
                    />
                  </div>
                ) : (
                  <>
                    <div className="w-48">
                      <FilterSelect
                        value={appointmentStatusFilter}
                        onChange={(v) => { setAppointmentStatusFilter(v); setOffset(0); }}
                        options={[
                          { value: 'all', label: 'Все статусы' },
                          { value: 'scheduled', label: 'Запланировано' },
                          { value: 'completed', label: 'Завершено' },
                          { value: 'cancelled', label: 'Отменено' },
                          { value: 'no_show', label: 'Не явился' },
                        ]}
                        testId="analytics-apt-status-filter"
                      />
                    </div>
                    <div className="w-48">
                      <FilterSelect
                        value={reminderFilter}
                        onChange={(v) => { setReminderFilter(v); setOffset(0); }}
                        options={[
                          { value: 'all', label: 'Все напоминания' },
                          { value: '24h', label: 'Отправлено 24ч' },
                          { value: '2h', label: 'Отправлено 2ч' },
                          { value: 'feedback', label: 'Обратная связь' },
                          { value: 'pending', label: 'Не отправлено' },
                        ]}
                        testId="analytics-reminder-filter"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Таблица */}
            {isLoading ? (
              <LoadingState text={
                activeTab === 'sent' ? 'Загрузка рассылок...' : 
                activeTab === 'scheduled' ? 'Загрузка запланированных...' :
                'Загрузка записей...'
              } />
            ) : activeTab === 'sent' ? (
              // Таблица отправленных
              broadcasts.length === 0 ? (
              <EmptyState
                title="Рассылки не найдены"
                description="Попробуйте изменить фильтры или период"
              />
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Дата отправки</TableHead>
                      <TableHead>Пациент</TableHead>
                      <TableHead>Телефон</TableHead>
                      <TableHead>Шаблон</TableHead>
                      <TableHead>Канал</TableHead>
                      <TableHead>Статус</TableHead>
                      <TableHead>Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {broadcasts.map((b) => (
                      <TableRow key={b.id}>
                        <TableCell>{formatDateTime(b.sent_at)}</TableCell>
                        <TableCell>{b.patient_name || '—'}</TableCell>
                        <TableCell>{formatPhone(b.phone)}</TableCell>
                        <TableCell>{b.template_name || b.template_slug || '—'}</TableCell>
                        <TableCell>{getMessengerBadge(b.messenger)}</TableCell>
                        <TableCell>{getStatusBadge(b.status)}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenChat(b.phone)}
                            testId={`broadcast-chat-${b.id}`}
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            Чат
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                  {/* Пагинация отправленных */}
                  {broadcastTotal > limit && (
                    <div className="flex items-center justify-between mt-4">
                      <div className="text-sm text-neutral-600">
                        Показано {offset + 1}—{Math.min(offset + limit, broadcastTotal)} из {broadcastTotal}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setOffset(Math.max(0, offset - limit))}
                          disabled={offset === 0}
                        >
                          Назад
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setOffset(offset + limit)}
                          disabled={offset + limit >= broadcastTotal}
                        >
                          Вперёд
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )
            ) : activeTab === 'scheduled' ? (
              // Таблица запланированных
              scheduled.length === 0 ? (
                <EmptyState
                  title="Запланированных сообщений нет"
                  description="Сообщения появятся после создания рассылки"
                />
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Запланировано на</TableHead>
                        <TableHead>Пациент</TableHead>
                        <TableHead>Телефон</TableHead>
                        <TableHead>Сообщение</TableHead>
                        <TableHead>Статус</TableHead>
                        <TableHead>Попытки</TableHead>
                        <TableHead>Действия</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {scheduled.map((s) => (
                        <TableRow key={s.id}>
                          <TableCell className="font-medium">{formatDateTime(s.scheduled_at)}</TableCell>
                          <TableCell>{s.patient_name || '—'}</TableCell>
                          <TableCell>{formatPhone(s.phone)}</TableCell>
                          <TableCell className="max-w-xs truncate">{s.content.substring(0, 50)}...</TableCell>
                          <TableCell>{getScheduledStatusBadge(s.status)}</TableCell>
                          <TableCell>{s.attempts}</TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenChat(s.phone)}
                              testId={`scheduled-chat-${s.id}`}
                            >
                              <MessageSquare className="w-4 h-4 mr-1" />
                              Чат
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Пагинация запланированных */}
                  {scheduledTotal > limit && (
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-neutral-600">
                      Показано {offset + 1}—{Math.min(offset + limit, scheduledTotal)} из {scheduledTotal}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setOffset(Math.max(0, offset - limit))}
                        disabled={offset === 0}
                      >
                        Назад
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setOffset(offset + limit)}
                        disabled={offset + limit >= scheduledTotal}
                      >
                        Вперёд
                      </Button>
                    </div>
                  </div>
                  )}
                </>
              )
            ) : (
              // Таблица по записям
              appointments.length === 0 ? (
                <EmptyState
                  title="Записей не найдено"
                  description="Попробуйте изменить фильтры или период"
                />
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Дата приёма</TableHead>
                        <TableHead>Пациент</TableHead>
                        <TableHead>Телефон</TableHead>
                        <TableHead>Врач</TableHead>
                        <TableHead>Статус</TableHead>
                        <TableHead>24ч</TableHead>
                        <TableHead>2ч</TableHead>
                        <TableHead>ОС</TableHead>
                        <TableHead>Действия</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {appointments.map((a) => (
                        <TableRow key={a.id}>
                          <TableCell className="font-medium">{formatDateTime(a.appointment_datetime)}</TableCell>
                          <TableCell>{a.patient_name || '—'}</TableCell>
                          <TableCell>{formatPhone(a.phone)}</TableCell>
                          <TableCell>{a.doctor_name || '—'}</TableCell>
                          <TableCell>{getAppointmentStatusBadge(a.status)}</TableCell>
                          <TableCell>
                            {a.reminder_24h_sent ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <BellOff className="w-4 h-4 text-neutral-300" />
                            )}
                          </TableCell>
                          <TableCell>
                            {a.reminder_2h_sent ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <BellOff className="w-4 h-4 text-neutral-300" />
                            )}
                          </TableCell>
                          <TableCell>
                            {a.feedback_sent ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <BellOff className="w-4 h-4 text-neutral-300" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenChat(a.phone)}
                              testId={`appointment-chat-${a.id}`}
                            >
                              <MessageSquare className="w-4 h-4 mr-1" />
                              Чат
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Пагинация по записям */}
                  {appointmentsTotal > limit && (
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-neutral-600">
                      Показано {offset + 1}—{Math.min(offset + limit, appointmentsTotal)} из {appointmentsTotal}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setOffset(Math.max(0, offset - limit))}
                        disabled={offset === 0}
                      >
                        Назад
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setOffset(offset + limit)}
                        disabled={offset + limit >= appointmentsTotal}
                      >
                        Вперёд
                      </Button>
                    </div>
                  </div>
                )}
              </>
              )
            )}
          </CardContent>
        </Card>
      </div>
      </LinearScreen>
    </MainLayout>
  );
}
